// ============================================================================
// barLinkedFields — the half of an adopted drink OrderUp OWNS
// ----------------------------------------------------------------------------
// An adopted drink carries two kinds of catalog field, and they follow
// different rules on purpose:
//
//   PRESENTATION — name, customer description, photo. The restaurant may reword
//   these; a publish reaches only the fields it never touched and flags the rest
//   (`barSyncMerge.ts`, the three-way merge).
//
//   RECIPE — the POS build card (`posDescription`), the ingredient list
//   (`ingredientIds`) and the stock-depleting BOM. These are the standardised
//   cocktail itself, which is the whole reason to adopt from the catalog, so they
//   are LINKED: every publish overwrites them at every adopting restaurant, and
//   the item editor shows them read-only. A restaurant that wants its own pour
//   builds its own item. Price, availability and colours stay the restaurant's.
//
// Pure and storage-free. The API applies these plans; the client item service
// applies `checkBarLinkedItemMutation`; neither re-decides the rule.
// ============================================================================

import type { IBarCatalogEntry, IBarCatalogSpecLine } from "./BarCatalogEntry";
import { formatBarPosDescription, type BarPourNames } from "./barBuildCard";

/**
 * Item fields a catalog publish owns outright on a linked drink. Exported so
 * the refusal, the editor lock and the docs name one list.
 */
export const BAR_LINKED_ITEM_FIELDS = ["posDescription", "ingredientIds"] as const;
export type BarLinkedItemField = (typeof BAR_LINKED_ITEM_FIELDS)[number];

/**
 * Why a save to a linked drink was refused. Readable on purpose: the editor
 * locks these fields, so reaching this means a stale tab or a bulk path, and the
 * operator should be told what happened rather than shown a code.
 */
export const BAR_LINKED_ITEM_REFUSAL =
  "This drink is linked to the OrderUp bar catalog. Its ingredients and POS description are kept up to date by OrderUp and cannot be edited here.";

/**
 * Why archiving or deleting a linked drink was refused.
 *
 * The drink is OrderUp's standard recipe, held in place by an adoption the
 * restaurant does not see from the items list. Archiving it would leave that
 * adoption syncing a drink nobody can find, and deleting it would erase the
 * subject of its own sales history. DISABLING is the restaurant's control: it
 * takes the drink off the POS and every delivery menu, and switches back on.
 */
export const BAR_LINKED_ITEM_REMOVAL_REFUSAL =
  "This drink comes from the OrderUp bar catalog, so it can't be archived or deleted. Disable it instead — a disabled drink stays off the POS and can be switched back on.";

/** The item as the linked-field rules read it. */
export interface IBarLinkedItemCurrent {
  readonly posDescription?: string | null;
  readonly ingredientIds?: readonly number[] | null;
  readonly barCatalogEntryGuid?: string | null;
  readonly isArchived?: boolean | null;
  readonly isActive?: boolean | null;
}

/** True when an item was adopted from the OrderUp bar catalog and is linked to it. */
export function isBarCatalogItem(
  item: { readonly barCatalogEntryGuid?: string | null } | null | undefined,
): boolean {
  return Boolean(item?.barCatalogEntryGuid);
}

export interface IBarLinkedItemUpdates {
  posDescription?: string;
  ingredientIds?: number[];
  barCatalogEntryGuid?: string;
}

export interface IBarLinkedFieldsPlan {
  /** Fields to write — empty when the item is already current. */
  readonly updates: IBarLinkedItemUpdates;
  readonly changed: boolean;
  /**
   * Spec-line ingredients with no platform `ingredientId`. While any remain the
   * ingredient list is NOT written: a partial list under-reports allergens,
   * which is worse than the list being visibly empty.
   */
  readonly unresolvedIngredientGuids: string[];
}

const sortedLines = (entry: IBarCatalogEntry): IBarCatalogSpecLine[] =>
  [...entry.specLines].sort((a, b) => a.sortOrder - b.sortOrder);

/**
 * Resolve a drink's spec to platform `ingredientId`s — every role, garnish and
 * rim included, because a lime wedge is an ingredient a guest can ask about.
 */
export function barEntryIngredientIds(
  entry: IBarCatalogEntry,
  ingredientIdByGuid: ReadonlyMap<string, number>,
): { ids: number[]; unresolved: string[] } {
  const ids: number[] = [];
  const unresolved: string[] = [];
  for (const line of sortedLines(entry)) {
    const id = ingredientIdByGuid.get(line.platformIngredientGuid);
    if (id === undefined) {
      if (!unresolved.includes(line.platformIngredientGuid)) {
        unresolved.push(line.platformIngredientGuid);
      }
      continue;
    }
    if (!ids.includes(id)) ids.push(id);
  }
  return { ids, unresolved };
}

/** Line endings and trailing whitespace are how a textarea round-trips text. */
const normText = (value: string | null | undefined): string =>
  (value ?? "").replace(/\r\n/g, "\n").trim();

const sameIdSet = (
  a: readonly number[] | null | undefined,
  b: readonly number[] | null | undefined,
): boolean => {
  const left = new Set(a ?? []);
  const right = new Set(b ?? []);
  return left.size === right.size && [...left].every((id) => right.has(id));
};

/**
 * What a publish writes onto ONE linked item.
 *
 * `pourNames` carries this location's CALL BRANDS, so the card it plans is the
 * card that location's till shows. Omitted, the plan is the standard recipe —
 * which is what every location without a call brand gets, byte for byte, so
 * adding this feature rewrites nothing for them.
 *
 * `ingredientIds` is deliberately NOT affected by a call brand: the platform
 * ingredient list drives ALLERGENS, and Combier is still triple sec. Swapping
 * a bottle changes what is poured, never what the drink contains for a guest
 * who asks.
 */
export function planBarLinkedFields(
  entry: IBarCatalogEntry,
  ingredientIdByGuid: ReadonlyMap<string, number>,
  current: IBarLinkedItemCurrent,
  pourNames?: BarPourNames,
): IBarLinkedFieldsPlan {
  const updates: IBarLinkedItemUpdates = {};

  const card = formatBarPosDescription(entry, pourNames);
  if (normText(current.posDescription) !== normText(card)) {
    updates.posDescription = card;
  }

  const { ids, unresolved } = barEntryIngredientIds(entry, ingredientIdByGuid);
  if (unresolved.length === 0 && !sameIdSet(current.ingredientIds, ids)) {
    updates.ingredientIds = ids;
  }

  if (current.barCatalogEntryGuid !== entry.guid) {
    updates.barCatalogEntryGuid = entry.guid;
  }

  return {
    updates,
    changed: Object.keys(updates).length > 0,
    unresolvedIngredientGuids: unresolved,
  };
}

/** The spec as a location experiences it — bottles, pours, order. */
const specIdentity = (entry: IBarCatalogEntry): string =>
  JSON.stringify(
    sortedLines(entry).map((l) => [
      l.platformIngredientGuid,
      l.quantityScaled,
      l.unit,
      l.role,
      l.optional,
    ]),
  );

/**
 * Whether an edit to a catalog entry changes anything an adopting restaurant
 * can see, and so must bump `version` and fan out.
 *
 * The build card is compared as TEXT because that is what reaches the till —
 * it already covers directions, glass, ice, garnish and every name and measure.
 * It is compared WITHOUT any location's call brands, deliberately: this asks
 * "did OrderUp change this drink", and a restaurant choosing its own bottle
 * must never queue a platform-wide fan-out.
 * The spec is compared separately because swapping a platform ingredient under
 * the same display name changes the ingredient list and the BOM while leaving
 * the card byte-identical.
 */
export function barEntryPublishesChange(
  before: IBarCatalogEntry,
  after: IBarCatalogEntry,
): boolean {
  return (
    normText(before.name) !== normText(after.name) ||
    normText(before.description) !== normText(after.description) ||
    normText(before.imageStoragePath) !== normText(after.imageStoragePath) ||
    formatBarPosDescription(before) !== formatBarPosDescription(after) ||
    specIdentity(before) !== specIdentity(after)
  );
}

/**
 * Refuse an ordinary item save that would change a field the catalog owns.
 *
 * Compares against the stored value rather than refusing the key, because the
 * item editor resends every field on every save — refusing the key would make a
 * linked drink impossible to reprice. Returns null when the save may proceed.
 */
export function checkBarLinkedItemMutation(
  current: IBarLinkedItemCurrent,
  next: IBarLinkedItemCurrent & Record<string, unknown>,
): typeof BAR_LINKED_ITEM_REFUSAL | typeof BAR_LINKED_ITEM_REMOVAL_REFUSAL | null {
  if (!isBarCatalogItem(current)) return null;

  // Archiving is refused; `isActive` is deliberately NOT checked — disabling is
  // exactly the control a restaurant has over a drink it does not own. Restoring
  // one that was archived before this rule existed is also allowed.
  if (next.isArchived === true && current.isArchived !== true) {
    return BAR_LINKED_ITEM_REMOVAL_REFUSAL;
  }

  if (
    "barCatalogEntryGuid" in next &&
    next.barCatalogEntryGuid !== current.barCatalogEntryGuid
  ) {
    return BAR_LINKED_ITEM_REFUSAL;
  }
  if (
    next.posDescription !== undefined &&
    normText(next.posDescription) !== normText(current.posDescription)
  ) {
    return BAR_LINKED_ITEM_REFUSAL;
  }
  if (
    next.ingredientIds !== undefined &&
    !sameIdSet(next.ingredientIds, current.ingredientIds)
  ) {
    return BAR_LINKED_ITEM_REFUSAL;
  }
  return null;
}

/**
 * Drop the catalog-owned keys from a payload bound for the item service.
 *
 * ⚠️ APPLY THIS **AFTER** SCHEMA VALIDATION, NEVER BEFORE — the order is the
 * whole point of the function existing.
 *
 * 🔴 THE DEFECT, FOUND IN PRODUCTION (House of Pho, 2026-09-20). `ItemModal`
 * deleted these two keys from its payload and THEN ran `ItemSchema.parse`.
 * `ItemSchema` declares `ingredientIds: z.array(z.number().int()).default([])`,
 * so Zod put the key straight back as `[]` — a payload claiming the operator
 * had cleared the drink's ingredient list. `checkBarLinkedItemMutation` refused
 * it, correctly, and so EVERY save of an adopted drink failed: price, name,
 * availability, colours and photo included, none of which the catalog owns.
 * The operator was shown a sentence about ingredients and a POS build card
 * while editing a PRICE, which reads as the product being broken rather than a
 * rule being enforced. Price is the restaurant's alone — see the header above.
 *
 * Deleting a key is therefore not enough on its own: anything that re-validates
 * the payload afterwards can resurrect it from a schema default. Strip LAST.
 */
export function withoutBarLinkedItemFields<T extends object>(
  payload: T,
): Omit<T, BarLinkedItemField> {
  const next = { ...payload } as Record<string, unknown>;
  for (const field of BAR_LINKED_ITEM_FIELDS) delete next[field];
  return next as Omit<T, BarLinkedItemField>;
}

/** One BOM line, as far as "did the recipe change" is concerned. */
export interface IBarRecipeLineLike {
  readonly inventoryGuid: string;
  readonly quantityScaled: number;
  readonly unit: string;
  readonly sortOrder: number;
  readonly wasteFactorE4?: number;
  readonly notes?: string;
  /** The spirit type the line pours as when its bottle is another type's house (`IRecipeItemIngredient.pourAsSpiritType`). */
  readonly pourAsSpiritType?: string;
}

/** True when two BOMs would deplete identically. */
export function recipeIngredientsEqual(
  a: readonly IBarRecipeLineLike[],
  b: readonly IBarRecipeLineLike[],
): boolean {
  if (a.length !== b.length) return false;
  const key = (l: IBarRecipeLineLike) =>
    [l.inventoryGuid, l.quantityScaled, l.unit, l.sortOrder, l.wasteFactorE4 ?? 0, l.notes ?? "", l.pourAsSpiritType ?? ""].join("|");
  const left = [...a].sort((x, y) => x.sortOrder - y.sortOrder).map(key);
  const right = [...b].sort((x, y) => x.sortOrder - y.sortOrder).map(key);
  return left.every((k, i) => k === right[i]);
}
